import type { DisclosureNewsApiRow } from '@app-types/instructionsApi';
import type {
  InstructionsCategory,
  InstructionsInfoItem,
} from '@app-types/instructions';
import { PLACEHOLDER } from '@utils/format';

const CATEGORY_BY_API_LABEL: Readonly<Record<string, InstructionsCategory>> = {
  all: 'すべて',
  earnings: '決算短信',
  dividend: '業績・配当',
  other: 'その他',
};

const toTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const mapDisclosureLabelToCategory = (
  label: unknown,
): InstructionsCategory => {
  const normalized = toTrimmedString(label);
  return normalized ? CATEGORY_BY_API_LABEL[normalized] ?? PLACEHOLDER : PLACEHOLDER;
};

const normalizeCompactTimestamp = (value: string): string => {
  const compactMatch = value.match(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})([+-])(\d{2})(\d{2})$/,
  );
  if (!compactMatch) return value;

  const [, year, month, day, hour, minute, second, sign, offsetHour, offsetMinute] =
    compactMatch;
  return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHour}:${offsetMinute}`;
};

const parseDisclosureTimestamp = (value: unknown): Date | null => {
  const normalized = toTrimmedString(value);
  if (!normalized) return null;

  const date = new Date(normalizeCompactTimestamp(normalized));
  return Number.isFinite(date.getTime()) ? date : null;
};

const TOKYO_CALENDAR_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const TOKYO_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Tokyo',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Formats the API timestamp explicitly in Japan time as `MM/DD HH:mm`. */
export const formatDisclosureCreatedAt = (value: unknown): string => {
  const date = parseDisclosureTimestamp(value);
  if (!date) return PLACEHOLDER;

  const parts = TOKYO_DATE_TIME_FORMATTER.formatToParts(date).reduce<Record<string, string>>(
    (result, part) => {
      if (part.type !== 'literal') result[part.type] = part.value;
      return result;
    },
    {},
  );

  return parts.month && parts.day && parts.hour && parts.minute
    ? `${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`
    : PLACEHOLDER;
};

/** Returns the disclosure's Japan calendar date as `YYYY-MM-DD`. */
export const getDisclosureJstDate = (value: unknown): string | null => {
  const date = parseDisclosureTimestamp(value);
  if (!date) return null;

  const parts = TOKYO_CALENDAR_DATE_FORMATTER.formatToParts(date).reduce<
    Record<string, string>
  >((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  return parts.year && parts.month && parts.day
    ? `${parts.year}-${parts.month}-${parts.day}`
    : null;
};

const mapDisclosureRow = (
  row: DisclosureNewsApiRow,
): InstructionsInfoItem | null => {
  const newsId = toTrimmedString(row.newsId);
  const pnac = toTrimmedString(row.pnac);
  const createdAtForKey = toTrimmedString(row.createdAt);
  const key = newsId ?? (
    pnac && createdAtForKey ? `${pnac}:${createdAtForKey}` : null
  );

  if (!key) return null;

  return {
    key,
    title: toTrimmedString(row.headline) ?? PLACEHOLDER,
    time: formatDisclosureCreatedAt(row.createdAt),
    category: mapDisclosureLabelToCategory(row.label),
    pnac,
    // Keep the exact wire value for the report-link request.
    createdAt: typeof row.createdAt === 'string' && row.createdAt.trim()
      ? row.createdAt
      : null,
  };
};

/** Maps rows in backend order without filtering, sorting, deduping, or repair. */
export const mapDisclosureNewsToInfoItems = (
  rows: readonly DisclosureNewsApiRow[] | null | undefined,
): InstructionsInfoItem[] => (
  rows?.reduce<InstructionsInfoItem[]>((items, row) => {
    const item = mapDisclosureRow(row);
    if (item) items.push(item);
    return items;
  }, []) ?? []
);
